/** @jsxImportSource @emotion/react */
import Pagination from 'components/atoms/Pagination'
import {
  NotMailedModal,
  useMarkStatementReturned,
} from 'components/lab/billingPatientStatements/MailedStatementActions'
import { reportSendResult } from 'components/lab/billingPatientStatements/sendResultToast'
import { DeliveryBadge } from 'components/lab/billingPatientStatements/DeliveryBadge'
import { describeMailing, formatDateTime, formatMoney } from 'components/lab/billingPatientStatements/statementFormat'
import { Popover } from 'components/atoms/Popover'
import Toast from 'components/atoms/Toast'
import React from 'react'
import {
  BillingPatientStatementDeliveryMethod,
  BillingPatientStatementListItem,
  StatementPrintRunItemStatus,
} from 'store/types'
import {
  useBatchSendPatientStatementsMutation,
  useGetPatientStatementsQuery,
  useSendPatientStatementMutation,
  useLazyGetStatementDownloadQuery,
} from 'store/services/patientStatements'

type Props = {
  patientId: string
}

const PAGE_SIZE = 12

export const PatientStatementsListing: React.FC<Props> = ({ patientId }) => {
  const [page, setPage] = React.useState(0)
  const [createdFrom, setCreatedFrom] = React.useState<string>('')
  const [createdTo, setCreatedTo] = React.useState<string>('')
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())
  const [activeRowPopover, setActiveRowPopover] = React.useState<string | null>(null)
  const [activeBatchPopover, setActiveBatchPopover] = React.useState<boolean>(false)
  const [sendStatement] = useSendPatientStatementMutation()
  const [batchSend, { isLoading: isSending }] = useBatchSendPatientStatementsMutation()
  const [getDownloadUrl] = useLazyGetStatementDownloadQuery()
  const [notMailedItemId, setNotMailedItemId] = React.useState<string | null>(null)
  const markReturned = useMarkStatementReturned()

  const { data, isFetching, refetch } = useGetPatientStatementsQuery({
    patientId: Number(patientId),
    page,
    size: 10,
    createdFrom: createdFrom || undefined,
    createdTo: createdTo || undefined,
  })

  const toggleSelectAll = () => {
    if (!data?.items?.length) return
    if (selectedIds.size === data.items.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(data.items.map((i) => i.id)))
    }
  }

  const toggleRow = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleSend = async (
    statement: BillingPatientStatementListItem,
    method?: BillingPatientStatementDeliveryMethod,
  ) => {
    try {
      await sendStatement({
        patientId: Number(patientId),
        statementId: statement.id,
        deliveryMethods: method ? [method] : [],
      }).unwrap()
      Toast({ type: 'success', label: 'Statement sent' })
      setActiveRowPopover(null)
      refetch()
    } catch (error) {
      if ((error as { data?: { message?: string } })?.data?.message) {
        Toast({ type: 'error', label: (error as { data: { message: string } }).data.message })
      }
    }
  }

  const handleDownload = async (statement: BillingPatientStatementListItem) => {
    try {
      const link = await getDownloadUrl({ statementId: statement.id, patientId: Number(patientId) })

      console.log(link)

      if (link.data?.url) {
        const printWindow = window.open(link.data?.url, '_blank')
        printWindow?.focus()
      }
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message || 'Unable to download'
      Toast({ type: 'error', label: message })
    }
  }

  const handleBatchSend = async (method: BillingPatientStatementDeliveryMethod) => {
    // Closed before the request, not after: a dropdown left open over a
    // multi-second send invites a second click and a second send.
    setActiveBatchPopover(false)

    try {
      const result = await batchSend({
        patientId: Number(patientId),
        statementIds: Array.from(selectedIds),
        deliveryMethods: [method],
      }).unwrap()

      reportSendResult(result)

      setSelectedIds(new Set())

      refetch()
    } catch (error) {
      if ((error as { data?: { message?: string } })?.data?.message) {
        Toast({ type: 'error', label: (error as { data: { message: string } }).data.message })
      }
    }
  }

  const renderDelivery = (statement: BillingPatientStatementListItem) => {
    const methods = (statement.deliveryMethods || []).filter(
      (method) => method !== BillingPatientStatementDeliveryMethod.MAIL,
    )
    const mailings = statement.mailings || []

    if (!methods.length && !mailings.length) {
      return '--'
    }

    return (
      <div css={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        {methods.map((method) => (
          <DeliveryBadge key={method} channel={method.toUpperCase()} label="Sent" color="#28a745" />
        ))}
        {mailings.map((mailing) => (
          <DeliveryBadge key={mailing.id} channel="PAPER" {...describeMailing(mailing)} />
        ))}
      </div>
    )
  }

  // The envelope a correction applies to: the latest one confirmed mailed.
  const mailedEnvelope = (statement: BillingPatientStatementListItem) =>
    (statement.mailings || []).find((mailing) => mailing.status === StatementPrintRunItemStatus.MAILED)

  return (
    <div>
      <div className="d-flex align-items-end" css={{ gap: '1rem', marginBottom: '1rem' }}>
        <div>
          <label className="textGray mb-1">Created From</label>
          <input
            type="date"
            className="form-control"
            value={createdFrom}
            onChange={(e) => setCreatedFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="textGray mb-1">Created To</label>
          <input
            type="date"
            className="form-control"
            value={createdTo}
            onChange={(e) => setCreatedTo(e.target.value)}
          />
        </div>
        <button
          className="btn btn-outline-secondary"
          onClick={() => {
            setPage(0)
            refetch()
          }}>
          Apply Filters
        </button>
        <div css={{ marginLeft: 'auto', position: 'relative' }}>
          <button
            className="btn btn-primary"
            disabled={!selectedIds.size || isSending}
            onClick={() => setActiveBatchPopover(!activeBatchPopover)}>
            {isSending ? 'Sending…' : 'Send Selected'}
          </button>
          <Popover open={activeBatchPopover} setOpen={setActiveBatchPopover}>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
              }}>
              <button
                className="btn btn-link text-left"
                disabled={isSending}
                onClick={() => handleBatchSend(BillingPatientStatementDeliveryMethod.SMS)}>
                Send SMS
              </button>
              <button
                className="btn btn-link text-left"
                disabled={isSending}
                onClick={() => handleBatchSend(BillingPatientStatementDeliveryMethod.EMAIL)}>
                Send Email
              </button>
            </div>
          </Popover>
        </div>
      </div>

      <div className="table-responsive" css={{ width: '100%', overflow: 'visible' }}>
        <table className="table table-sm" style={{ width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <input
                  type="checkbox"
                  checked={!!data?.items?.length && selectedIds.size === (data?.items?.length || 0)}
                  onChange={toggleSelectAll}
                />
              </th>
              <th>Statement Date</th>
              <th>Balance</th>
              <th>Paid?</th>
              <th>Delivery Method & Status</th>
              <th>Last Sent</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {isFetching && (
              <tr>
                <td colSpan={8} className="text-center">
                  Loading...
                </td>
              </tr>
            )}
            {!isFetching && !data?.items?.length && (
              <tr>
                <td colSpan={8} className="text-center">
                  No statements
                </td>
              </tr>
            )}
            {data?.items?.map((statement) => (
              <tr key={statement.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(statement.id)}
                    onChange={() => toggleRow(statement.id)}
                  />
                </td>
                <td>{formatDateTime(statement.createdAt)}</td>
                <td>{formatMoney(statement.balance)}</td>
                <td>
                  {statement.voidedAt ? (
                    <span
                      css={{
                        color: '#6c757d',
                        fontWeight: 600,
                      }}
                      title="Statement was voided because no balance is due">
                      Voided
                    </span>
                  ) : (
                    <span
                      css={{
                        color: statement.paid ? '#28a745' : '#dc3545',
                        fontWeight: 600,
                      }}>
                      {statement.paid ? 'Yes' : 'No'}
                    </span>
                  )}
                </td>
                <td>{renderDelivery(statement)}</td>
                <td>{formatDateTime(statement.lastSentAt)}</td>
                <td style={{ position: 'relative' }}>
                  <button
                    className="btn btn-link p-0"
                    onClick={() =>
                      setActiveRowPopover(activeRowPopover === statement.id ? null : statement.id)
                    }>
                    <i className="fas fa-ellipsis-h" />
                  </button>
                  <Popover
                    open={activeRowPopover === statement.id}
                    setOpen={(x) => {
                      if (!x) setActiveRowPopover(null)

                      setActiveRowPopover(x ? statement.id : null)
                    }}>
                    <div
                      css={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}>
                      {!statement.voidedAt && (
                        <>
                          <button
                            className="btn btn-link text-left"
                            onClick={() =>
                              handleSend(statement, BillingPatientStatementDeliveryMethod.SMS)
                            }>
                            Resend SMS
                          </button>
                          <button
                            className="btn btn-link text-left"
                            onClick={() =>
                              handleSend(statement, BillingPatientStatementDeliveryMethod.EMAIL)
                            }>
                            Resend Email
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-link text-left"
                        onClick={() => handleDownload(statement)}>
                        Download PDF
                      </button>
                      {mailedEnvelope(statement) && (
                        <>
                          <div className="dropdown-divider" />
                          <button
                            className="btn btn-link text-left"
                            onClick={() => {
                              setActiveRowPopover(null)
                              setNotMailedItemId(mailedEnvelope(statement)!.id)
                            }}>
                            Mark not mailed
                          </button>
                          <button
                            className="btn btn-link text-left"
                            onClick={() => {
                              setActiveRowPopover(null)
                              markReturned(mailedEnvelope(statement)!.id)
                            }}>
                            Mark returned
                          </button>
                        </>
                      )}
                    </div>
                  </Popover>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        onPageChange={setPage}
        totalCount={data?.totalItems}
        currentPage={page}
        pageSize={PAGE_SIZE}
      />
      <NotMailedModal itemId={notMailedItemId} onHide={() => setNotMailedItemId(null)} />
    </div>
  )
}
